import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { createCourseEnrollmentCheckout } from '@/lib/stripeConnect';
import { devLog, devError } from '@/utils/devLog';
import { formatPrice } from '@/utils/formatters';

// Define specific types for our data
interface CourseCheckoutData {
  id: string;
  title: string;
  price: number;
}

export default function Checkout() {
  const { username, courseSlug } = useParams<{ username?: string; courseSlug?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [courseData, setCourseData] = useState<CourseCheckoutData | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Extract course data from location state or fetch it
  const courseId = location.state?.courseId;
  const courseName = location.state?.courseName;
  const coursePrice = location.state?.coursePrice;
  const from = location.state?.from;
  const previousPath = location.state?.previousPath;

  // Check if we're in a cancel state
  const isCancel = location.search.includes("cancel=true");

  // Log key information on component mount
  useEffect(() => {
    devLog("[Checkout] Component mounted", {
      url: window.location.href,
      path: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      isGlobalCheckout: window.location.pathname === '/global-checkout',
      isCourseSpecificCheckout: username && courseSlug && window.location.pathname === `/${username}/${courseSlug}/checkout`,
      params: { username, courseSlug },
      state: { courseId, courseName, coursePrice, from },
      timestamp: new Date().toISOString()
    });
    
    // Add this logging to debug navigation
    console.debug('[Checkout] Page loaded, state:', {
      locationState: location.state,
      queryString: location.search,
      referrer: document.referrer,
      previousPath: previousPath,
      user: user?.id ? 'authenticated' : 'unauthenticated'
    });
    
    // Store the referrer as a fallback return path
    if (document.referrer && document.referrer.includes(window.location.origin)) {
      sessionStorage.setItem('checkoutReferrer', document.referrer);
    }
    
    // If this is a cancel state, automatically navigate back to the course page
    if (isCancel) {
      handleGoBack();
      return;
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCourseData = async () => {
      if (!username && !courseSlug && window.location.pathname === "/global-checkout") {
        // Global checkout flow - we must have location state
        if (!courseId) {
          devError("[Checkout] Missing courseId in global checkout", {
            pathname: window.location.pathname,
            locationState: location.state
          });
          toast.error("Course information missing");
          navigate('/');
          return;
        }

        devLog("[Checkout] Using course data from location state (global checkout)", {
          courseId,
          courseName,
          coursePrice
        });
            
        setCourseData({
          id: courseId,
          title: courseName || "Course",
          price: coursePrice || 0
        });
        setLoading(false);
        return;
      }
      
      // Course-specific checkout flow
      if (!username || !courseSlug) {
        devLog("[Checkout] Missing username or courseSlug params", {
          username,
          courseSlug,
          pathname: window.location.pathname
        });
        toast.error("Course information missing");
        navigate('/');
        return;
      }

      setLoading(true);
      try {
        // If we already have course data from state, use it
        if (courseId && courseName) {
          devLog("[Checkout] Using course data from location state", {
            courseId,
            courseName,
            coursePrice
          });
          
          setCourseData({
            id: courseId,
            title: courseName,
            price: coursePrice || 0
          });
          setLoading(false);
          return;
        }

        // Otherwise fetch course data
        devLog("[Checkout] Fetching course data from API", {
          username,
          courseSlug
        });
        
        const { data: rpcData, error } = await supabase.rpc(
          'get_public_course',
          { p_username: username, p_slug: courseSlug }
        );

        if (error || !rpcData) {
          throw new Error(error?.message || "Course not found");
        }

        // Type-safe access to nested fields
        // Handle the case where rpcData is an object with a course property
        const rpcResult = rpcData as { course?: { id: string; title: string; price: number } };

        if (!rpcResult.course) {
          devError("[Checkout] Course data is incomplete", { rpcData });
          throw new Error("Course data is incomplete");
        }

        devLog("[Checkout] Successfully fetched course data", {
          courseId: rpcResult.course.id,
          courseTitle: rpcResult.course.title
        });

        setCourseData({
          id: rpcResult.course.id,
          title: rpcResult.course.title,
          price: rpcResult.course.price || 0,
        });
      } catch (error) {
        devError("[Checkout] Error fetching course:", error);
        toast.error("Failed to load course information");
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    if (!isCancel) {
      fetchCourseData();
    }
  }, [username, courseSlug, courseId, courseName, coursePrice, navigate, location.state, isCancel]);

  const handleProceedToPayment = async () => {
    if (!courseData || !user) {
      console.debug('[Checkout] Missing data for payment:', { 
        hasCourseData: !!courseData, 
        hasUser: !!user
      });
      return;
    }
    
    try {
      setProcessingPayment(true);
      console.debug('[Checkout] Starting payment process for course:', { 
        courseId: courseData.id,
        courseName: courseData.title,
        price: courseData.price
      });
      
      // Get checkout URL from Stripe
      const checkoutUrl = await createCourseEnrollmentCheckout(
        courseData.id,
        user.id,
        courseData.price,
        courseData.title,
        username,
        courseSlug
      );
      
      console.debug('[Checkout] Received checkout URL:', checkoutUrl?.substring(0, 30) + '...');
      
      if (checkoutUrl) {
        console.debug('[Checkout] Redirecting to Stripe checkout');
        window.location.href = checkoutUrl;
      } else {
        console.error('[Checkout] No checkout URL returned');
        toast.error('Failed to create checkout session');
        setProcessingPayment(false);
      }
    } catch (error) {
      console.error('[Checkout] Payment error:', error);
      toast.error('Failed to process payment');
      setProcessingPayment(false);
    }
  };

  const handleGoBack = () => {
    console.debug('[Checkout] Going back');
    // Try to use the 'from' path from location state first
    if (from) {
      console.debug('[Checkout] Navigating to stored "from" path:', from);
      navigate(from, { replace: true });
      return;
    }

    // Otherwise try to use stored referrer
    const referrer = sessionStorage.getItem('checkoutReferrer');
    if (referrer) {
      console.debug('[Checkout] Navigating to stored referrer:', referrer);
      window.location.href = referrer;
      return;
    }

    // If username/courseSlug are available, go back to course page
    if (username && courseSlug) {
      console.debug('[Checkout] Navigating to course page from params:', `/${username}/${courseSlug}`);
      navigate(`/${username}/${courseSlug}`, { replace: true });
      return;
    }

    // Final fallback to home
    console.debug('[Checkout] No return path found, going to home');
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md p-8 space-y-4">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-12 w-full mt-8" />
        </div>
      </div>
    );
  }

  if (!courseData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md p-8">
          <h1 className="text-2xl font-bold mb-4">Course not found</h1>
          <p className="text-muted-foreground mb-6">
            We couldn't find the course you're looking for. Please try again or contact support.
          </p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container px-4 mx-auto max-w-3xl">
        <Card className="p-6 shadow-lg">
          {loading ? (
            <div className="w-full max-w-md p-8 space-y-4">
              <Skeleton className="h-8 w-64 mb-4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-12 w-full mt-8" />
            </div>
          ) : courseData ? (
            <div className="space-y-6">
              <h1 className="text-2xl font-bold">Complete Your Purchase</h1>
              <div className="border-t border-b py-4">
                <h2 className="font-medium mb-2">Order Summary</h2>
                <div className="flex justify-between items-center">
                  <span>{courseData.title}</span>
                  <span className="font-bold">${formatPrice(courseData.price)}</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center font-bold text-lg">
                <span>Total:</span>
                <span>${formatPrice(courseData.price)}</span>
              </div>
              
              {/* CRITICAL FIX: Add ID to button for debugging */}
              <Button 
                id="stripe-checkout-button"
                onClick={handleProceedToPayment} 
                className="w-full"
                size="lg"
                disabled={processingPayment}
              >
                {processingPayment ? (
                  <>Processing...</>
                ) : (
                  <>Proceed to Payment</>
                )}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleGoBack}
                className="w-full mt-2"
              >
                Go Back
              </Button>

              {/* CRITICAL FIX: Add debug panel in development */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-8 p-4 border border-dashed border-gray-300 bg-gray-50 rounded text-xs">
                  <h3 className="font-mono font-bold">Debug Info:</h3>
                  <pre className="whitespace-pre-wrap break-all">
                    {JSON.stringify({
                      courseId: courseData.id,
                      courseTitle: courseData.title, 
                      coursePrice: courseData.price,
                      path: window.location.pathname,
                      fromState: location.state?.from || 'none',
                    }, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <h2 className="text-xl font-bold mb-2">Course Not Found</h2>
              <p className="text-muted-foreground mb-4">We couldn't find the course information.</p>
              <Button onClick={() => navigate('/')}>Return Home</Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
